'use client';

import useApiRequest from 'components/UserInvoice/BelegAssistant/useApiRequest';
import { objectToFormData } from 'helpers/api';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import type { CommitResult, ImportDocument, ImportField, ImportValues, ImportValuesPatch } from './types';

/** One request per file, API Gateway caps a Lambda call at ~6MB. */
const UPLOAD_CONCURRENCY = 3;

const POLL_INTERVAL_MS = 3000;

interface Response {
    data: ImportDocument[];
    fields: ImportField[];
}

const isEmptyValue = (value: unknown) =>
    null === value || undefined === value || '' === value || (Array.isArray(value) && value.length === 0);

/** Mirrors the controller's mergeValues(): per key, empty removes. */
const applyPatch = (current: ImportValues | null, patch: ImportValuesPatch): ImportValues => {
    const formData = { ...(current?.form_data ?? {}) };
    Object.entries(patch.form_data ?? {}).forEach(([name, value]) => {
        if (isEmptyValue(value)) delete formData[name];
        else formData[name] = value;
    });

    return {
        name: 'name' in patch ? patch.name ?? null : current?.name ?? null,
        email: 'email' in patch ? patch.email ?? null : current?.email ?? null,
        form_data: formData
    };
};

/**
 * The import rows of one event and everything that changes them.
 *
 * Rows live on the server, so closing the modal loses nothing and a second admin sees the same list.
 */
export const useBookingImportDocuments = (eventId: number) => {
    const api = useApiRequest();
    const basePath = `booking/${eventId}/document-import`;
    const [pendingUploads, setPendingUploads] = useState<{ uid: string; name: string }[]>([]);

    const { data, mutate, isLoading } = useSWR<Response>(`${basePath}/documents`, {
        refreshInterval: (latest) =>
            pendingUploads.length > 0 ||
            (latest?.data ?? []).some((doc) => 'pending' === doc.status || 'processing' === doc.status)
                ? POLL_INTERVAL_MS
                : 0,
        // Rows are edited in place; a refetch on focus would only race the edits.
        revalidateOnFocus: false
    });

    const documents = useMemo(() => data?.data ?? [], [data]);
    const fields = useMemo(() => data?.fields ?? [], [data]);

    const replaceDocuments = useCallback(
        (changed: ImportDocument[]) =>
            mutate((prev) => {
                if (!prev) return prev;
                const byId = new Map(changed.map((doc) => [doc.id, doc]));
                const known = new Set(prev.data.map((doc) => doc.id));

                return {
                    ...prev,
                    data: [
                        ...prev.data.map((doc) => byId.get(doc.id) ?? doc),
                        ...changed.filter((doc) => !known.has(doc.id))
                    ]
                };
            }, false),
        [mutate]
    );

    const removeDocuments = useCallback(
        (ids: number[]) =>
            mutate(
                (prev) => (prev ? { ...prev, data: prev.data.filter((doc) => !ids.includes(doc.id)) } : prev),
                false
            ),
        [mutate]
    );

    const uploadFiles = useCallback(
        async (files: File[]) => {
            if (files.length === 0) return;

            const queued = files.map((file) => ({
                uid: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
                name: file.name,
                file
            }));
            setPendingUploads((prev) => [...prev, ...queued.map(({ uid, name }) => ({ uid, name }))]);

            const remaining = [...queued];
            const worker = async () => {
                for (;;) {
                    const next = remaining.shift();
                    if (!next) return;

                    const response = await api<{ data: ImportDocument }>({
                        url: `${basePath}/documents`,
                        method: 'POST',
                        data: objectToFormData({ file: next.file })
                    });
                    if (response?.data) void replaceDocuments([response.data]);
                    setPendingUploads((prev) => prev.filter((item) => item.uid !== next.uid));
                }
            };

            await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queued.length) }, worker));
            void mutate();
        },
        [api, basePath, mutate, replaceDocuments]
    );

    const updateDocument = useCallback(
        async (id: number, values: ImportValuesPatch) => {
            // grids and multi-selects build the next value from this cache, a stale one drops quick clicks
            void mutate(
                (prev) =>
                    prev
                        ? {
                              ...prev,
                              data: prev.data.map((doc) =>
                                  doc.id === id ? { ...doc, values: applyPatch(doc.values, values) } : doc
                              )
                          }
                        : prev,
                false
            );

            const response = await api<{ data: ImportDocument }>({
                url: `${basePath}/documents/${id}`,
                method: 'PATCH',
                data: { values },
                muted: true
            });
            if (response?.data) void replaceDocuments([response.data]);
        },
        [api, basePath, mutate, replaceDocuments]
    );

    const bulkUpdate = useCallback(
        async (ids: number[], values: ImportValuesPatch) => {
            const response = await api<{ data: ImportDocument[] }>({
                url: `${basePath}/documents/bulk`,
                method: 'PATCH',
                data: { ids, values }
            });
            if (response?.data) void replaceDocuments(response.data);
        },
        [api, basePath, replaceDocuments]
    );

    const retryDocument = useCallback(
        async (id: number) => {
            const response = await api<{ data: ImportDocument }>({
                url: `${basePath}/documents/${id}/retry`,
                method: 'POST'
            });
            if (response?.data) void replaceDocuments([response.data]);
        },
        [api, basePath, replaceDocuments]
    );

    const discardDocument = useCallback(
        async (id: number) => {
            const response = await api({ url: `${basePath}/documents/${id}`, method: 'DELETE' });
            if (null !== response) void removeDocuments([id]);
        },
        [api, basePath, removeDocuments]
    );

    const commit = useCallback(
        (ids: number[]) => api<CommitResult>({ url: `${basePath}/commit`, method: 'POST', data: { ids } }),
        [api, basePath]
    );

    return {
        basePath,
        documents,
        fields,
        isLoading,
        pendingUploads,
        uploadFiles,
        updateDocument,
        bulkUpdate,
        retryDocument,
        discardDocument,
        commit,
        removeDocuments,
        reload: mutate
    };
};

export default useBookingImportDocuments;
